import { FC, useState, useEffect } from 'react'
import ProgressBar from '@ramonak/react-progress-bar'
import styled, { css, keyframes } from 'styled-components'
import { useTranslation } from 'react-i18next'
import TipsListSkeleton from './TipsListSkeleton'

interface LoaderProps {
  withOverlay?: boolean;
  className?: string;
  isRelative?: boolean;
  noTransparency?: boolean;
  text?: string;
  tipsLoading?: boolean;
}
const ProgreeBarLoader: FC<LoaderProps> = ({
  withOverlay,
  className,
  isRelative,
  text,
  noTransparency,
  tipsLoading
}) => {
  const [ completed, Setcompleted ] = useState(20)
  const maxCompleted = 100

  const { t } = useTranslation()

  useEffect(
    () => {
      const timer = setTimeout(() => Setcompleted(completed > maxCompleted? 80: completed*2), 1000)
      return () => {
        clearTimeout(timer)
      }
    },
    [ completed ]
  )

  return (
    <LoaderContainer
      className={className}
      $withOverlay={withOverlay}
      $isRelative={isRelative}
      $noTransparency={noTransparency}
    >
      <LoaderTitle>{t('ui.holdOnPlease')}</LoaderTitle>
      <LoaderImage />
      <ProgressBarContainer>
        <LoadingText>
          {text ? text : t('feed.fetchingMatches')}
          <DotTyping>
            <span></span>
          </DotTyping>
        </LoadingText>
        <ProgressBar
          completed={completed}
          maxCompleted={maxCompleted}
          bgColor={'#212121'}
          customLabel={' '}
        />
      </ProgressBarContainer>
    </LoaderContainer>
  )
}

interface LoaderProps {
  withOverlay?: boolean;
  className?: string;
  isRelative?: boolean;
  showBanner?: boolean;
  noTransparency?: boolean;
}

export const SpinnerLoader: FC<LoaderProps> = ({
  withOverlay,
  className,
  isRelative,
  showBanner,
  noTransparency
}) => {
  return (
    <LoaderContainer
      className={className}
      $withOverlay={withOverlay}
      $isRelative={isRelative}
      $noTransparency={noTransparency}
    >
      <LoaderSpinner />
    </LoaderContainer>
  )
}

const dotWave = keyframes`
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1.2);
    opacity: 1;
  }
`

const DotTyping = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 6px;
  margin-left: 8px;

  &::before,
  &::after {
    content: '';
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: #666;
    animation: ${dotWave} 1.4s infinite ease-in-out;
  }

  &::before {
    animation-delay: -0.32s;
  }

  &::after {
    animation-delay: -0.16s;
  }

  /* Middle dot */
  span {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: #666;
    animation: ${dotWave} 1.4s infinite ease-in-out;
    animation-delay: 0s;
  }
`

const LoadingText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-tertiary);
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`

export const LoaderContainer = styled.div<{ $withOverlay: boolean, $isRelative?: boolean, $noTransparency: boolean }>`
  position: ${props => props.$isRelative ? 'relative' : 'fixed'};
  max-width: 600px;
  margin: 0 auto;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background-color: var(--bg-quaternary);
  z-index: ${(props) => (props.$withOverlay ? 1000 : undefined)};
  backdrop-filter: ${(props) => (props.$withOverlay ? 'blur(50px)' : undefined)};
  backdrop-filter: ${(props) => (props.$withOverlay ? 'blur(50px)' : undefined)};

  ${(props) =>
    props.$noTransparency &&
    css`
      opacity: 1;
      background-color: var(--bg-quaternary);
      width: 100%

    `}
`
export const LoaderSpinner = styled.div`
  width: 40px;
  height: 40px;
  animation: rotate 2s linear infinite;
  background-repeat: no-repeat;
  background-position: center;
  background-image: url('/assets/icons/icon-spinner.png');
  filter: var(--loader-spinner-filter);

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`

export const LoaderTitle = styled.h2 `
  padding-bottom: 5%;
`

export const LoaderImage = styled.div`
  width: 100%;
  height: 170px;
  background-repeat: no-repeat;
  background-position: center;
  background-image: url('/assets/icons/icon-384x384_transparent.png');
`

export const ProgressBarContainer = styled.div `
  width: 400px;
  padding-top: 6%;
  text-align: center;
  .dot-typing {
    position: relative;
    left: -9999px;
    width: 5px;
    max-width: 5px;
    height: 5px;
    border-radius: 5px;
    color: #212121;
    animation: dot-typing 1.5s infinite ease-in-out;
    display: inline-flex;
    margin-left: 20px;
  }
  h4 {
    padding: 5px;
    text-transform: capitalize;
  }
  
  @keyframes dot-typing {
    0% {
      box-shadow: 9984px 0 0 0 #212121, 9984px 0 0 0 #212121, 9984px 0 0 0 #212121;
    }
    16.667% {
      box-shadow: 9984px 0 0 0 #212121, 9999px 0 0 0 #212121;
    }
    33.333% {
      box-shadow: 9984px 0 0 0 #212121, 9999px 0 0 0 #212121, 10014px 0 0 0 #212121;
    }
    50% {
      box-shadow: 9984px 0 0 0 #212121, 9999px 0 0 0 #212121, 10014px 0 0 0 #212121;
    }
    66.667% {
      box-shadow: 9984px 0 0 0 #212121, 9999px 0 0 0 #212121;
    }
    83.333% {
      box-shadow: 9984px 0 0 0 #212121;
    }
    100% {
      box-shadow: 0 0 0 0 0;
    }
  }
  `

const Loader: FC<LoaderProps> = ({
  withOverlay,
  className,
  isRelative,
  noTransparency,
  text,
}) => {
  const props = {
    isRelative,
    className,
    withOverlay,
    text,
    noTransparency
  }


  return <TipsListSkeleton />
}

export default Loader